import {
  getActiveProfile,
  getGlobalConfigPath,
  normalizeBaseUrl,
  readGlobalConfig,
  removeGlobalProfile,
  upsertGlobalProfile,
  useGlobalProfile,
  validateProfileName,
} from '../config';
import { Data, Effect } from 'effect';

export class ProfileCommandError extends Data.TaggedError('ProfileCommandError')<{
  readonly method: string;
  readonly status?: number;
  readonly cause: unknown;
}> {}

function runProfileBoundary<A>(method: string, run: () => A): A {
  return Effect.runSync(
    Effect.try({
      try: run,
      catch: (cause) => new ProfileCommandError({ method, cause }),
    }).pipe(Effect.catch((error) => Effect.fail(error.cause))),
  );
}

function writeStdout(message: string): void {
  process.stdout.write(message + '\n');
}

function writeStderr(message: string): void {
  process.stderr.write(message + '\n');
}

export interface ProfileAddOptions {
  baseUrl?: string;
  apiKey?: string;
  use?: boolean;
}

export function profileAdd(name: string, options: ProfileAddOptions): void {
  return runProfileBoundary('profileAdd', () => profileAddSync(name, options));
}

function profileAddSync(name: string, options: ProfileAddOptions): void {
  const profileName = validateProfileName(name);
  if (!options.baseUrl) {
    writeStderr('Error: --base-url is required');
    process.exit(1);
  }

  const baseUrl = normalizeBaseUrl(options.baseUrl);
  upsertGlobalProfile(
    profileName,
    {
      baseUrl,
      ...(options.apiKey ? { apiKey: options.apiKey.trim() } : {}),
    },
    { use: options.use === true },
  );

  writeStdout(`Saved profile "${profileName}" to ${getGlobalConfigPath()}`);
  writeStdout(`Base URL: ${baseUrl}`);
  if (options.use) {
    writeStdout(`Active profile: ${profileName}`);
  } else {
    writeStdout('');
    writeStdout('Use it with:');
    writeStdout(`  okra profile use ${profileName}`);
  }
}

export function profileUse(name: string): void {
  return runProfileBoundary('profileUse', () => profileUseSync(name));
}

function profileUseSync(name: string): void {
  const profileName = validateProfileName(name);
  useGlobalProfile(profileName);
  writeStdout(`Active profile: ${profileName}`);
}

export function profileCurrent(): void {
  return runProfileBoundary('profileCurrent', profileCurrentSync);
}

function profileCurrentSync(): void {
  const config = readGlobalConfig();
  const active = getActiveProfile(config);
  if (!active) {
    writeStdout('No active profile');
    return;
  }

  writeStdout(`Active profile: ${active.name}`);
  if (active.profile.baseUrl) {
    writeStdout(`Base URL: ${active.profile.baseUrl}`);
  }
  writeStdout(`API key: ${active.profile.apiKey ? 'configured' : 'not configured in profile'}`);
}

export function profileList(): void {
  return runProfileBoundary('profileList', profileListSync);
}

function profileListSync(): void {
  const config = readGlobalConfig();
  const profiles = config?.profiles || {};
  const names = Object.keys(profiles).sort();
  if (names.length === 0) {
    writeStdout('No profiles configured');
    return;
  }

  for (const name of names) {
    const marker = name === config?.activeProfile ? '*' : ' ';
    const profile = profiles[name];
    writeStdout(`${marker} ${name} ${profile.baseUrl || '(no base URL)'}`);
  }
}

export function profileRemove(name: string): void {
  return runProfileBoundary('profileRemove', () => profileRemoveSync(name));
}

function profileRemoveSync(name: string): void {
  const profileName = validateProfileName(name);
  removeGlobalProfile(profileName);
  writeStdout(`Removed profile "${profileName}"`);
}
